import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { createErpSocket } from "../utils/socketIo";
import {
  getUserPermissions,
  PERMISSIONS,
  HRMS_ROUTE_ANY_PERMISSIONS,
  hasPermission,
} from "../utils/permissions";
import {
  getUserData,
  encryptData,
  setUserPermissions,
  getCurrentPinType,
} from "./const";
import {
  hasErpPermission,
  getErpToken,
  applyRealtimeSessionFromAdminUserSocket,
  getErpTenantBranding,
  getTenantBrandImageSrc,
} from "../erpClient";
import { getTenantNameDisplay } from "../utils/tenantName";
import {
  hasWhatsAppPermission,
  WHATSAPP_PERMISSION_CODES,
} from "../utils/whatsappPermissions";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import useIsCurrentFinancialYear, {
  FY_ADD_DISABLED_TITLE,
} from "../hooks/useIsCurrentFinancialYear";
import CashbookQuickPopup from "../components/CashbookQuickPopup";
import BankbookQuickPopup from "../components/BankbookQuickPopup";
import ThemeTintedLogo from "../components/ThemeTintedLogo";
import { isDesktopMode } from "../desktop/desktopEnv";

const COLLAPSED_LOGO_SRC = `${process.env.PUBLIC_URL || ""
  }/assets/images/logos/sidebaricon.png`;
const EXPANDED_LOGO_SRC = `${process.env.PUBLIC_URL || ""
  }/assets/images/logos/logo_1.png`;

const MENU_LABEL_CLASS = "hide-menu text-truncate flex-grow-1 min-w-0";
const SIDEBAR_SCROLL_IDLE_MS = 700;

function normalizeSidebarSearchText(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s\-_/]/g, "");
}

/** Jewellery ERP sidebar icons — unique Solar icons, shop/gold/stock themed. */
const SIDEBAR_ICONS = {
  dashboard: "solar:shop-2-line-duotone",
  leads: "solar:user-speak-rounded-line-duotone",
  followUps: "solar:calendar-mark-line-duotone",
  reminders: "solar:bell-bing-line-duotone",
  salesTarget: "solar:target-line-duotone",
  hrms: "solar:case-round-line-duotone",
  walkIn: "solar:walking-line-duotone",
  customerAssign: "solar:user-check-rounded-line-duotone",
  orderQueue: "solar:clipboard-list-line-duotone",
  purchase: "solar:delivery-line-duotone",
  oldMetalPurchase: "solar:scale-line-duotone",
  metalTransfer: "solar:transfer-horizontal-line-duotone",
  refinery: "solar:fire-line-duotone",
  sales: "solar:bag-check-line-duotone",
  wholesale: "solar:box-minimalistic-line-duotone",
  customOrder: "solar:cart-large-minimalistic-line-duotone",
  repairingOrder: "solar:sledgehammer-line-duotone",
  jangad: "solar:export-line-duotone",
  cashIn: "solar:hand-money-line-duotone",
  cashOut: "solar:cash-out-line-duotone",
  bankIn: "solar:card-recive-line-duotone",
  bankOut: "solar:card-send-line-duotone",
  products: "solar:box-line-duotone",
  huidBarcode: "solar:qr-code-line-duotone",
  customer: "solar:user-circle-line-duotone",
  supplierKarigar: "solar:user-hands-line-duotone",
  metalGroup: "solar:medal-ribbon-star-line-duotone",
  itemCategory: "solar:layers-minimalistic-line-duotone",
  otherCharge: "solar:tag-price-line-duotone",
  productGroup: "solar:widget-6-line-duotone",
  openingPool: "solar:database-line-duotone",
  productAssorting: "solar:filters-line-duotone",
  billType: "solar:document-text-line-duotone",
  shopExpense: "solar:wallet-2-line-duotone",
  bank: "solar:banknote-2-line-duotone",
  capitalInvestment: "solar:pie-chart-2-line-duotone",
  tenantBranch: "solar:buildings-2-line-duotone",
  dailyPrice: "solar:chart-2-line-duotone",
  addSettlement: "solar:calculator-minimalistic-line-duotone",
  activityCalendar: "solar:calendar-add-line-duotone",
  role: "solar:user-id-line-duotone",
  employee: "solar:users-group-rounded-line-duotone",
  girviDashboard: "solar:safe-circle-line-duotone",
  girviList: "solar:crown-line-duotone",
  girviQuickPayment: "solar:wallet-money-linear",
  girviBank: "solar:banknote-2-linear",
  masterData: "solar:folder-with-files-line-duotone",
  back: "solar:arrow-left-line-duotone",
  waInbox: "solar:letter-unread-line-duotone",
  waPhonebooks: "mdi:card-account-phone-outline",
  waTemplates: "solar:document-text-line-duotone",
  waCampaigns: "solar:plain-2-line-duotone",
  waDashboard: "solar:chart-2-line-duotone",
  // Scheme
  schemeDashboard: "solar:star-circle-line-duotone",
  schemePlans: "solar:document-add-line-duotone",
  schemeCards: "solar:card-line-duotone",
  schemeTransactions: "solar:hand-money-line-duotone",
  paymentTransaction: "solar:bill-list-line-duotone",
  scheme: "solar:gift-line-duotone",
};

const MASTER_DATA_ROUTES = [
  "/metalgrouptable",
  "/itemcategorytable",
  "/otherchargestable",
  "/productgrouptable",
  "/metaltransfer",
  "/bankdetailstable",
  "/capitalinvestment",
  "/salestarget",
  "/hrms",
  "/customertable",
  "/karigar",
  "/tenantbranch",
  "/shopexpense",
  "/branchexpense",
  "/roletable",
  "/addrole",
  "/editrole",
  "/employee",
];

const SidebarDivider = () => (
  <li>
    <span className="sidebar-divider lg" />
  </li>
);

function isMasterDataRoute(pathname) {
  return MASTER_DATA_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`),
  );
}

function Sidebar({ currentModule = "Home", isSidebarCollapsed = false }) {
  const isCurrentFinancialYear = useIsCurrentFinancialYear();
  // Get user permissions
  const userPermissions = getUserPermissions();
  const [socket, setSocket] = useState(null);
  const [userData, setUserData] = useState(getUserData());
  const [forceUpdate, setForceUpdate] = useState(0);
  const [currentPinType, setCurrentPinType] = useState(getCurrentPinType());
  const [tenantBranding, setTenantBranding] = useState(() => getErpTenantBranding());
  const tenantNameDisplay = getTenantNameDisplay();

  const location = useLocation();
  const { buttonTableStyle, buttonClass } = useButtonTableStyle();
  const [showCashPopup, setShowCashPopup] = useState(false);
  const [cashFlowType, setCashFlowType] = useState("cash-in");
  const [showBankPopup, setShowBankPopup] = useState(false);
  const [bankFlowType, setBankFlowType] = useState("bank-in");
  const sidebarNavRef = useRef(null);
  const sidebarSearchRef = useRef(null);
  const sidebarScrollIdleTimerRef = useRef(null);
  const [isSidebarNavScrolling, setIsSidebarNavScrolling] = useState(false);
  const [menuSearch, setMenuSearch] = useState("");
  const [menuSearchNoResults, setMenuSearchNoResults] = useState(false);
  const [showMasterDataMenu, setShowMasterDataMenu] = useState(() =>
    isMasterDataRoute(location.pathname),
  );

  // Check if Dihran module is active
  const isDihranModule = currentModule === "Dihran";
  const isWhatsAppModule = location.pathname.startsWith("/whatsapp");
  const isSchemeModule = location.pathname.startsWith("/scheme");
  const isQuotationModule = currentPinType === "quote";

  // Function to close sidebar on mobile view
  const closeMobileSidebar = () => {
    // Check if window width is less than 1300px (mobile/tablet view)
    if (window.innerWidth < 1300) {
      // Remove show-sidebar class from main-wrapper (for main sidebar)
      const mainWrapper = document.getElementById("main-wrapper");
      if (mainWrapper && mainWrapper.classList.contains("show-sidebar")) {
        mainWrapper.classList.remove("show-sidebar");
      }

      // Also close Bootstrap offcanvas if it exists (for mobile navbar)
      const mobileNavbar = document.getElementById("mobilenavbar");
      if (mobileNavbar) {
        // Check if Bootstrap is available and close offcanvas properly
        if (
          typeof window.bootstrap !== "undefined" &&
          window.bootstrap.Offcanvas
        ) {
          const offcanvasInstance =
            window.bootstrap.Offcanvas.getInstance(mobileNavbar);
          if (offcanvasInstance) {
            offcanvasInstance.hide();
          }
        } else {
          // Fallback: manually remove show class and backdrop
          mobileNavbar.classList.remove("show");
          const backdrop = document.querySelector(".offcanvas-backdrop");
          if (backdrop) {
            backdrop.remove();
          }
          document.body.classList.remove("offcanvas-open");
          document.body.style.overflow = "";
          document.body.style.paddingRight = "";
        }
      }
    }
  };

  // Socket.IO listener for real-time updates
  useEffect(() => {
    const hasLegacyToken = !!localStorage.getItem("token");
    const hasErpSession = !!getErpToken();
    if (!hasLegacyToken && !hasErpSession) {
      return undefined;
    }

    const newSocket = createErpSocket();
    setSocket(newSocket);

    newSocket.on("employee_permission_updated", (data) => {
      applyRealtimeSessionFromAdminUserSocket(data);

      if (!data?.employee) return;

      const updatedEmployee = {
        ua_id: data.employee.id || data.employee.ua_id,
        name: data.employee.name || "",
        phone: data.employee.phone || "",
        type: parseInt(data.employee.usertype || data.employee.type) || 0,
        status: parseInt(data.employee.status) || 0,
        email: data.employee.email || "",
      };

      // Check if the updated employee is the currently logged-in user
      if (
        updatedEmployee.ua_id === userData?.id ||
        updatedEmployee.ua_id === userData?.ua_id
      ) {
        // Update logged-in user's data for real-time permission changes
        const updatedUserData = {
          ...userData,
          usertype: updatedEmployee.type,
          status: updatedEmployee.status,
        };

        setUserData(updatedUserData);

        // Also update the persistent storage so changes persist after page refresh
        try {
          // Get current user data and update only permission fields
          const currentUserData = getUserData();
          if (currentUserData) {
            const updatedStorageData = {
              ...currentUserData,
              usertype: updatedEmployee.type,
              status: updatedEmployee.status,
            };

            // Re-encrypt and store using the same method as getUserData/setUserData
            localStorage.setItem("userData", encryptData(updatedStorageData));

            // CRITICAL: Extract and update permissions from socket data
            const permissionFields = [
              "daily_price",
              "bank",
              "supplier",
              "employee",
              "customer_arrival",
              "customer_assign",
              "customer",
              "metal_group",
              "item_category",
              "product_group",
              "stock",
              "stock_table",
              "edit_stock",
              "bill_type",
              "shop_expense_table",
              "add_shop_expense",
              "edit_shop_expense",
              "sales",
              "invoice",
              "edit_invoice",
              "karigar",
              "custom_order_entry",
              "custom_order_entry_table",

              "bulk_stock",
              "repairing_order",
              "order_queue",
              "sales_target_view",
              "sales_target_edit",
              "dashboard",
              "purchase_table",
              "add_purchase",
              "edit_purchase",
              "metal_purchase_table",
              "add_old_purchase",
              "edit_old_purchase",
              "edit_custom_order_entry",
              "core_products",
              "product_assorting_table",
              "repairing_order_entry",
              "edit_repairing_order",
              "jangad",
              "add_jangad",
              "edit_jangad",
              "refinery",
              "add_refinery",
              "edit_refinery",
              "add_settlement",
              "overall_ledger",
              "rojmel_view",
              "purchase_ledger",
              "urd_ledger",
              "sales_ledger",
              "karigar_ledger",
              "bank_ledger",
              "customer_ledger",
              "girvi",
              "girvitable",
              "addgirvi",
              "editgirvi",
              "girviquickpayment",
              "girvibankdetails",
              "girvibankledger",
              "reversegirvi",
              "reversegirvitable",
              "addreversegirvi",
              "editreversegirvi",
              "reversegirviquickpayment",
              "REVERSE_GIRVI_DASHBOARD_VIEW",
              "REVERSE_GIRVI_ADD",
              "REVERSE_GIRVI_EDIT",
              "REVERSE_GIRVI_VIEW",
              "REVERSE_GIRVI_QUICK_PAYMENT_VIEW",
              "capital_investment_table",
              "edit_capital_investment",
              "tenant_table",
              "edit_tenant",
              "branch_table",
              "edit_branch",
            ];

            // Extract permissions from socket data
            const updatedPermissions = {};
            permissionFields.forEach((field) => {
              if (data.employee[field] !== undefined) {
                updatedPermissions[field] = data.employee[field];
              }
            });

            // Update permissions in localStorage
            setUserPermissions(updatedPermissions);

            // Dispatch custom event to notify all components to refresh permissions
            window.dispatchEvent(
              new CustomEvent("permissionsUpdated", {
                detail: {
                  usertype: updatedEmployee.type,
                  status: updatedEmployee.status,
                  permissions: updatedPermissions,
                },
              }),
            );
          }
        } catch (error) {
          console.error("Error updating stored user data:", error);
        }

        // Force re-render of sidebar to update permissions
        setForceUpdate((prev) => prev + 1);
      }
    });

    return () => {
      newSocket.off("employee_permission_updated");
      newSocket.disconnect();
    };
  }, [userData]);

  useEffect(() => {
    const bump = () => setForceUpdate((p) => p + 1);
    window.addEventListener("erpAuthUpdated", bump);
    return () => window.removeEventListener("erpAuthUpdated", bump);
  }, []);

  useEffect(() => {
    const handleBranding = (e) => {
      setTenantBranding(e.detail?.tenant ?? getErpTenantBranding());
    };
    window.addEventListener("erpTenantBrandingUpdated", handleBranding);
    return () => window.removeEventListener("erpTenantBrandingUpdated", handleBranding);
  }, []);

  // Listen for permission updates from other components
  useEffect(() => {
    const handlePermissionsUpdate = () => {
      // Force re-render by updating forceUpdate state
      setForceUpdate((prev) => prev + 1);
    };

    window.addEventListener("permissionsUpdated", handlePermissionsUpdate);
    return () => {
      window.removeEventListener("permissionsUpdated", handlePermissionsUpdate);
    };
  }, []);

  // Keep sidebar in sync with quote/access mode without requiring a refresh.
  useEffect(() => {
    const handlePinTypeChange = (event) => {
      const nextPinType = event?.detail?.pinType || getCurrentPinType();
      setCurrentPinType(nextPinType);
    };

    const handleStorageChange = (event) => {
      if (event.key === "currentPinType") {
        setCurrentPinType(event.newValue || getCurrentPinType());
      }
    };

    window.addEventListener("apiUrlChanged", handlePinTypeChange);
    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("apiUrlChanged", handlePinTypeChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  // Close sidebar when route changes (for mobile view)
  useEffect(() => {
    closeMobileSidebar();
    setMenuSearch("");
  }, [location.pathname]);

  useEffect(() => {
    const dock = document.querySelector(".left-sidebar");
    const nav = sidebarNavRef.current;
    if (!dock || !nav) return undefined;

    const isPremium = () =>
      document.documentElement.getAttribute("data-theme") === "premium";

    const iconItems = () =>
      Array.from(nav.querySelectorAll("li.sidebar-item")).filter((li) => {
        if (li.style.display === "none") return false;
        if (li.querySelector(".sidebar-divider")) return false;
        return Boolean(li.querySelector(".sidebar-link"));
      });

    let restCenters = [];
    let rafId = 0;
    let pendingX = 0;

    const sizeIcons = () => {
      if (!isPremium()) return;
      const count = Math.max(iconItems().length + 1, 1);
      const size = Math.max(28, Math.min(40, Math.floor((window.innerWidth - 96) / count)));
      dock.style.setProperty("--dock-icon-size", `${size}px`);
    };

    const resetZoom = () => {
      iconItems().forEach((li) => {
        const link = li.querySelector(".sidebar-link");
        ["flex", "width", "min-width", "max-width", "height", "margin"].forEach(
          (prop) => li.style.removeProperty(prop),
        );
        if (link) {
          link.style.removeProperty("width");
          link.style.removeProperty("height");
          link.style.transform = "";
          link.style.zIndex = "";
        }
      });
      restCenters = [];
    };

    const cacheCenters = () => {
      restCenters = iconItems().map((li) => {
        const rect = li.getBoundingClientRect();
        return rect.left + rect.width / 2;
      });
    };

    const applyMagnify = () => {
      rafId = 0;
      if (!isPremium()) return;
      const items = iconItems();
      if (!restCenters.length || restCenters.length !== items.length) {
        cacheCenters();
      }
      const base =
        parseFloat(getComputedStyle(dock).getPropertyValue("--dock-icon-size")) ||
        36;
      const radius = base * 3.2;
      const maxScale = 1.85;

      items.forEach((li, index) => {
        const dist = Math.abs(pendingX - restCenters[index]);
        const t = Math.max(0, 1 - dist / radius);
        const scale = 1 + (maxScale - 1) * (t * t);
        const spread = ((scale - 1) * base) / 2;
        const link = li.querySelector(".sidebar-link");
        li.style.margin = `0 ${spread}px`;
        if (link) {
          link.style.transform = `translateY(${(1 - scale) * 6}px) scale(${scale})`;
          link.style.zIndex = String(Math.round(scale * 10));
        }
      });
    };

    const onEnter = () => {
      if (!isPremium()) return;
      cacheCenters();
    };

    const onMove = (event) => {
      if (!isPremium()) return;
      pendingX = event.clientX;
      if (!rafId) rafId = requestAnimationFrame(applyMagnify);
    };

    sizeIcons();
    dock.addEventListener("mouseenter", onEnter);
    dock.addEventListener("mousemove", onMove);
    dock.addEventListener("mouseleave", resetZoom);
    window.addEventListener("resize", sizeIcons);
    document.addEventListener("themeChanged", sizeIcons);

    return () => {
      dock.removeEventListener("mouseenter", onEnter);
      dock.removeEventListener("mousemove", onMove);
      dock.removeEventListener("mouseleave", resetZoom);
      window.removeEventListener("resize", sizeIcons);
      document.removeEventListener("themeChanged", sizeIcons);
      if (rafId) cancelAnimationFrame(rafId);
      resetZoom();
      dock.style.removeProperty("--dock-icon-size");
    };
  }, [menuSearch, location.pathname, currentModule]);

  useEffect(() => {
    if (
      !isDihranModule &&
      !isWhatsAppModule &&
      !isSchemeModule &&
      isMasterDataRoute(location.pathname)
    ) {
      setShowMasterDataMenu(true);
    }
  }, [location.pathname, isDihranModule, isWhatsAppModule, isSchemeModule]);

  useEffect(() => {
    const nav = sidebarNavRef.current;
    if (!nav) return;

    const query = normalizeSidebarSearchText(menuSearch);
    const items = nav.querySelectorAll("li.sidebar-item");
    let visibleCount = 0;

    items.forEach((item) => {
      const labelEl = item.querySelector(".hide-menu");
      const label = normalizeSidebarSearchText(labelEl?.textContent);
      const matches = !query || label.includes(query);
      item.style.display = matches ? "" : "none";
      if (matches) visibleCount += 1;
    });

    nav.querySelectorAll("li:not(.sidebar-item)").forEach((li) => {
      if (li.querySelector(".sidebar-divider")) {
        li.style.display = query ? "none" : "";
      }
    });

    setMenuSearchNoResults(query.length > 0 && visibleCount === 0);
  }, [
    menuSearch,
    forceUpdate,
    isDihranModule,
    isWhatsAppModule,
    isSchemeModule,
    isQuotationModule,
    currentModule,
    showMasterDataMenu,
  ]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (isDesktopMode()) {
        return;
      }
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        sidebarSearchRef.current?.focus();
        sidebarSearchRef.current?.select();
      }
      if (
        event.key === "Escape" &&
        document.activeElement === sidebarSearchRef.current
      ) {
        setMenuSearch("");
        sidebarSearchRef.current?.blur();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    const navEl = sidebarNavRef.current;
    if (!navEl) return undefined;

    const handleScroll = () => {
      setIsSidebarNavScrolling(true);
      if (sidebarScrollIdleTimerRef.current) {
        clearTimeout(sidebarScrollIdleTimerRef.current);
      }
      sidebarScrollIdleTimerRef.current = setTimeout(() => {
        setIsSidebarNavScrolling(false);
      }, SIDEBAR_SCROLL_IDLE_MS);
    };

    navEl.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      navEl.removeEventListener("scroll", handleScroll);
      if (sidebarScrollIdleTimerRef.current) {
        clearTimeout(sidebarScrollIdleTimerRef.current);
      }
    };
  }, []);

  // Inject Girvi theme styles when Girvi module is active
  useEffect(() => {
    if (isDihranModule) {
      // Create or update style element for Girvi theme
      let styleElement = document.getElementById("girvi-sidebar-theme");
      if (!styleElement) {
        styleElement = document.createElement("style");
        styleElement.id = "girvi-sidebar-theme";
        document.head.appendChild(styleElement);
      }
      styleElement.textContent = `
        .left-sidebar {
          z-index: 1030 !important;
        }
        [data-theme="vintage"] .left-sidebar .sidebar-item.active > .sidebar-link,
        [data-theme="vintage"] .left-sidebar .sidebar-link.active {
          background-color: var(--theme-primary, #000000) !important;
          color: #ffffff !important;
          border: 1px solid var(--theme-primary, #000000) !important;
          border-left: 4px solid var(--theme-primary, #000000) !important;
          border-radius: 0 !important;
          box-shadow: none !important;
        }
        [data-theme="vintage"] .left-sidebar .sidebar-item.active > .sidebar-link:hover,
        [data-theme="vintage"] .left-sidebar .sidebar-link.active:hover {
          background-color: var(--theme-primary, #000000) !important;
          color: #ffffff !important;
          border-color: var(--theme-primary, #000000) !important;
          border-left-color: var(--theme-primary, #000000) !important;
          border-radius: 0 !important;
          box-shadow: none !important;
        }
      `;
    } else {
      // Remove Girvi theme styles when not in Girvi module
      const styleElement = document.getElementById("girvi-sidebar-theme");
      if (styleElement) {
        styleElement.remove();
      }
    }
  }, [isDihranModule]);

  // Simple function to check if a path is active
  const isActive = (path) => {
    if (path === "/roletable") {
      return (
        location.pathname === "/roletable" ||
        location.pathname === "/addrole" ||
        location.pathname === "/editrole" ||
        location.pathname.startsWith("/roletable/") ||
        location.pathname.startsWith("/addrole/") ||
        location.pathname.startsWith("/editrole/")
      );
    }
    if (path === "/shopexpense") {
      return (
        location.pathname === "/shopexpense" ||
        location.pathname === "/branchexpense" ||
        location.pathname.startsWith("/shopexpense/") ||
        location.pathname.startsWith("/branchexpense/")
      );
    }
    return location.pathname === path;
  };

  const isWhatsAppPathActive = (path) => {
    if (path === "/whatsapp") {
      return location.pathname.startsWith("/whatsapp");
    }
    if (path === "/whatsapp/inbox") {
      return (
        location.pathname === "/whatsapp/inbox" ||
        location.pathname.startsWith("/whatsapp/inbox/")
      );
    }
    return (
      location.pathname === path || location.pathname.startsWith(`${path}/`)
    );
  };

  // Helper function to render menu item if user has permission.
  // OR with ERP code, or with permissionsAny (user needs at least one).
  const renderMenuItem = (
    permission,
    children,
    erpPermissionCode,
    permissionsAny,
  ) => {
    const hasSingle =
      permission != null && permission !== undefined
        ? hasPermission(permission)
        : false;
    const hasErp =
      erpPermissionCode && typeof erpPermissionCode === "string"
        ? hasErpPermission(erpPermissionCode)
        : false;
    const hasAny =
      Array.isArray(permissionsAny) && permissionsAny.length > 0
        ? permissionsAny.some((p) => hasPermission(p))
        : false;

    if (hasSingle || hasErp || hasAny) {
      return children;
    }
    return null;
  };

  // Helper function to get sidebar link classes with styling
  const getSidebarLinkClass = (path) => {
    const active = path.startsWith("/whatsapp/")
      ? isWhatsAppPathActive(path)
      : isActive(path);
    const baseClass = `sidebar-link d-flex align-items-center gap-2 ${active ? "active" : ""
      }`;
    if (active) {
      const styleClass =
        buttonTableStyle === "curve" ? "active-curve" : "active-square";
      return `${baseClass} ${styleClass}`;
    }
    return baseClass;
  };

  const openCashPopup = (type) => {
    if (!hasPermission(PERMISSIONS.CASH_BANK_ADD) && !hasPermission(PERMISSIONS.CASH_BANK_VIEW)) {
      toast.error("You do not have permission to access Cash In/Out.");
      return;
    }
    if (!isCurrentFinancialYear) {
      toast.error(FY_ADD_DISABLED_TITLE);
      return;
    }
    setShowBankPopup(false);
    setCashFlowType(type);
    setShowCashPopup(true);
  };

  const closeCashPopup = () => {
    setShowCashPopup(false);
  };

  const openBankPopup = (type) => {
    if (!hasPermission(PERMISSIONS.CASH_BANK_ADD) && !hasPermission(PERMISSIONS.CASH_BANK_VIEW)) {
      toast.error("You do not have permission to access Bank In/Out.");
      return;
    }
    if (!isCurrentFinancialYear) {
      toast.error(FY_ADD_DISABLED_TITLE);
      return;
    }
    setShowCashPopup(false);
    setBankFlowType(type);
    setShowBankPopup(true);
  };

  const closeBankPopup = () => {
    setShowBankPopup(false);
  };

  const showCashQuickActions =
    hasPermission(PERMISSIONS.CASH_BANK_VIEW) ||
    hasPermission(PERMISSIONS.CASH_BANK_ADD);
  const showBankQuickActions =
    hasPermission(PERMISSIONS.CASH_BANK_VIEW) ||
    hasPermission(PERMISSIONS.CASH_BANK_ADD);
  const showProductsInNavbar = hasPermission(PERMISSIONS.STOCK_TABLE);
  const showHuidInNavbar = hasPermission(PERMISSIONS.STOCK_TABLE);
  const showShopExpenseInNavbar =
    hasPermission(PERMISSIONS.SHOP_EXPENSE_TABLE) ||
    hasPermission(PERMISSIONS.ADD_SHOP_EXPENSE);
  const showWhatsAppInbox = hasWhatsAppPermission(
    WHATSAPP_PERMISSION_CODES.INBOX,
  );
  const showWhatsAppPhonebooks = hasWhatsAppPermission(
    WHATSAPP_PERMISSION_CODES.PHONEBOOK,
  );
  const showWhatsAppTemplates = hasWhatsAppPermission(
    WHATSAPP_PERMISSION_CODES.TEMPLATE,
  );
  const showWhatsAppCampaigns = hasWhatsAppPermission(
    WHATSAPP_PERMISSION_CODES.CAMPAIGN,
  );
  const showWhatsAppDashboard = hasWhatsAppPermission(
    WHATSAPP_PERMISSION_CODES.DASHBOARD,
  );

  const showGirviDashboard = hasPermission(PERMISSIONS.GIRVI_DASHBOARD_VIEW);
  const showGirviTable =
    hasPermission(PERMISSIONS.GIRVITABLE) ||
    hasPermission(PERMISSIONS.GIRVI_VIEW) ||
    hasPermission(PERMISSIONS.GIRVI) ||
    hasPermission(PERMISSIONS.GIRVI_ADD) ||
    hasPermission(PERMISSIONS.GIRVI_EDIT) ||
    hasPermission("girvitable") ||
    hasPermission("GIRVI_TABLE");
  const showGirviQuickPayment =
    hasPermission(PERMISSIONS.GIRVIQUICKPAYMENT) ||
    hasPermission(PERMISSIONS.GIRVI_QUICK_PAYMENT_VIEW) ||
    hasPermission("girviquickpayment") ||
    hasPermission("GIRVI_QUICK_PAYMENT");
  const showGirviBank =
    hasPermission(PERMISSIONS.GIRVIBANKDETAILS) ||
    hasPermission(PERMISSIONS.GIRVI_BANK_VIEW) ||
    hasPermission(PERMISSIONS.GIRVI_BANK_ADD) ||
    hasPermission(PERMISSIONS.GIRVI_BANK_EDIT) ||
    hasPermission("girvibankdetails") ||
    hasPermission("GIRVI_BANK_DETAILS");
  const showReverseGirviDashboard = hasPermission(
    PERMISSIONS.REVERSE_GIRVI_DASHBOARD_VIEW,
  );
  const showReverseGirviTable =
    hasPermission(PERMISSIONS.REVERSE_GIRVI_VIEW) ||
    hasPermission(PERMISSIONS.REVERSE_GIRVI_ADD) ||
    hasPermission(PERMISSIONS.REVERSE_GIRVI_EDIT);
  const showReverseGirviQuickPayment = hasPermission(
    PERMISSIONS.REVERSE_GIRVI_QUICK_PAYMENT_VIEW,
  );
  const hasAnyMasterDataPermission =
    hasPermission(PERMISSIONS.METAL_GROUP) ||
    hasPermission(PERMISSIONS.ITEM_CATEGORY) ||
    hasPermission(PERMISSIONS.OTHER_CHARGE) ||
    hasPermission(PERMISSIONS.PRODUCT_GROUP) ||
    hasPermission(PERMISSIONS.METAL_TRANSFER_VIEW) ||
    hasPermission(PERMISSIONS.BANK) ||
    hasPermission(PERMISSIONS.CAPITAL_INVESTMENT_TABLE) ||
    hasPermission(PERMISSIONS.SHOP_EXPENSE_TABLE) ||
    hasPermission(PERMISSIONS.ADD_SHOP_EXPENSE) ||
    hasPermission(PERMISSIONS.SALES_TARGET_VIEW) ||
    hasPermission(PERMISSIONS.SALES_TARGET_EDIT) ||
    HRMS_ROUTE_ANY_PERMISSIONS.some((permission) =>
      hasPermission(permission),
    ) ||
    hasPermission(PERMISSIONS.CUSTOMER) ||
    hasPermission(PERMISSIONS.SUPPLIER) ||
    hasPermission(PERMISSIONS.ROLE_VIEW) ||
    hasPermission(PERMISSIONS.EMPLOYEE) ||
    hasPermission(PERMISSIONS.TENANT_TABLE) ||
    hasPermission(PERMISSIONS.BRANCH_TABLE);

  // Home menu section visibility — divider under a section only if that section has items
  // and at least one later section is also visible.
  const showHomeTopSection =
    hasPermission(PERMISSIONS.DASHBOARD) ||
    hasPermission(PERMISSIONS.DAILY_PRICE) ||
    hasErpPermission("DAILY_PRICE");
  const showHomeMasterSection = hasAnyMasterDataPermission;
  const showHomeWalkInSection =
    hasPermission(PERMISSIONS.CUSTOMER_ARRIVAL) ||
    hasPermission(PERMISSIONS.CUSTOMER_ASSIGN) ||
    hasPermission(PERMISSIONS.ORDER_QUEUE);
  const showHomeBankSection = showBankQuickActions;
  const showHomeTradeSection =
    hasPermission(PERMISSIONS.PURCHASE_TABLE) ||
    hasPermission(PERMISSIONS.ADD_PURCHASE) ||
    hasPermission(PERMISSIONS.METAL_PURCHASE_TABLE) ||
    hasPermission(PERMISSIONS.SALES) ||
    hasPermission(PERMISSIONS.INVOICE) ||
    hasPermission(PERMISSIONS.WHSALE_VIEW) ||
    hasPermission(PERMISSIONS.WHSALE_ADD) ||
    hasPermission(PERMISSIONS.CUSTOM_ORDER_ENTRY_TABLE) ||
    hasPermission(PERMISSIONS.REPAIRING_ORDER) ||
    hasPermission(PERMISSIONS.JANGAD);
  const showHomeOpsSection =
    hasPermission(PERMISSIONS.PRODUCT_ASSORTING_TABLE) ||
    hasPermission(PERMISSIONS.REFINERY) ||
    hasPermission(PERMISSIONS.STOCK_TABLE) ||
    hasPermission(PERMISSIONS.STOCK_CHECK_VIEW);
  const showHomeCrmSection =
    hasPermission(PERMISSIONS.LEADS_VIEW) ||
    hasPermission(PERMISSIONS.REMINDERS_VIEW);
  const showHomeSettlementSection =
    hasPermission(PERMISSIONS.CUSTOMER_SETTLEMENT_VIEW) ||
    hasPermission(PERMISSIONS.CUSTOMER_SETTLEMENT_ADD) ||
    hasPermission(PERMISSIONS.CUSTOMER_SETTLEMENT_EDIT);

  const anyHomeAfter = (...flags) => flags.some(Boolean);
  const showDividerAfterHomeTop = showHomeTopSection &&
    anyHomeAfter(
      showHomeMasterSection,
      showHomeWalkInSection,
      showHomeBankSection,
      showHomeTradeSection,
      showHomeOpsSection,
      showHomeCrmSection,
      showHomeSettlementSection,
    );
  const showDividerAfterHomeMaster = showHomeMasterSection &&
    anyHomeAfter(
      showHomeWalkInSection,
      showHomeBankSection,
      showHomeTradeSection,
      showHomeOpsSection,
      showHomeCrmSection,
      showHomeSettlementSection,
    );
  const showDividerAfterHomeWalkIn = showHomeWalkInSection &&
    anyHomeAfter(
      showHomeBankSection,
      showHomeTradeSection,
      showHomeOpsSection,
      showHomeCrmSection,
      showHomeSettlementSection,
    );
  const showDividerAfterHomeBank = showHomeBankSection &&
    anyHomeAfter(
      showHomeTradeSection,
      showHomeOpsSection,
      showHomeCrmSection,
      showHomeSettlementSection,
    );
  const showDividerAfterHomeTrade = showHomeTradeSection &&
    anyHomeAfter(
      showHomeOpsSection,
      showHomeCrmSection,
      showHomeSettlementSection,
    );
  const showDividerAfterHomeOps = showHomeOpsSection &&
    anyHomeAfter(showHomeCrmSection, showHomeSettlementSection);
  const showDividerAfterHomeCrm =
    showHomeCrmSection && showHomeSettlementSection;

  // Master-data sub-menu section visibility
  const showMdProductGroups =
    hasPermission(PERMISSIONS.METAL_GROUP) ||
    hasPermission(PERMISSIONS.ITEM_CATEGORY) ||
    hasPermission(PERMISSIONS.OTHER_CHARGE) ||
    hasPermission(PERMISSIONS.PRODUCT_GROUP) ||
    hasPermission(PERMISSIONS.METAL_TRANSFER_VIEW);
  const showMdFinance =
    hasPermission(PERMISSIONS.BANK) ||
    hasPermission(PERMISSIONS.CAPITAL_INVESTMENT_TABLE) ||
    hasPermission(PERMISSIONS.SHOP_EXPENSE_TABLE) ||
    hasPermission(PERMISSIONS.ADD_SHOP_EXPENSE);
  const showMdSalesHrms =
    hasPermission(PERMISSIONS.SALES_TARGET_VIEW) ||
    hasPermission(PERMISSIONS.SALES_TARGET_EDIT) ||
    HRMS_ROUTE_ANY_PERMISSIONS.some((permission) => hasPermission(permission));
  const showMdParties =
    hasPermission(PERMISSIONS.CUSTOMER) ||
    hasPermission(PERMISSIONS.SUPPLIER);
  const showMdTenant =
    hasPermission(PERMISSIONS.TENANT_TABLE) ||
    hasPermission(PERMISSIONS.BRANCH_TABLE);
  const showMdRole = hasPermission(PERMISSIONS.EMPLOYEE);
  const showMdEmployee = hasPermission(PERMISSIONS.EMPLOYEE);

  const anyMdAfter = (...flags) => flags.some(Boolean);
  const showDividerAfterMdBack = anyMdAfter(
    showMdProductGroups,
    showMdFinance,
    showMdSalesHrms,
    showMdParties,
    showMdTenant,
    showMdRole,
    showMdEmployee,
  );
  const showDividerAfterMdProductGroups = showMdProductGroups &&
    anyMdAfter(
      showMdFinance,
      showMdSalesHrms,
      showMdParties,
      showMdTenant,
      showMdRole,
      showMdEmployee,
    );
  const showDividerAfterMdFinance = showMdFinance &&
    anyMdAfter(showMdSalesHrms, showMdParties, showMdTenant, showMdRole, showMdEmployee);
  const showDividerAfterMdSalesHrms = showMdSalesHrms &&
    anyMdAfter(showMdParties, showMdTenant, showMdRole, showMdEmployee);
  const showDividerAfterMdParties = showMdParties &&
    anyMdAfter(showMdTenant, showMdRole, showMdEmployee);
  const showDividerAfterMdTenant = showMdTenant && (showMdRole || showMdEmployee);

  // Quotation module section visibility
  const showQuoteTopSection = hasPermission(PERMISSIONS.DASHBOARD);
  const showQuoteTradeSection =
    hasPermission(PERMISSIONS.SALES) ||
    hasPermission(PERMISSIONS.INVOICE) ||
    hasPermission(PERMISSIONS.WHSALE_VIEW) ||
    hasPermission(PERMISSIONS.WHSALE_ADD) ||
    hasPermission(PERMISSIONS.CUSTOM_ORDER_ENTRY_TABLE) ||
    hasPermission(PERMISSIONS.REPAIRING_ORDER);
  const showQuoteBankProductsSection =
    showBankQuickActions ||
    (!showProductsInNavbar && hasPermission(PERMISSIONS.STOCK_TABLE));
  const showQuoteHuidSection =
    !showHuidInNavbar && hasPermission(PERMISSIONS.STOCK_TABLE);
  const showDividerAfterQuoteTop =
    showQuoteTopSection &&
    anyHomeAfter(
      showQuoteTradeSection,
      showQuoteBankProductsSection,
      showQuoteHuidSection,
    );
  const showDividerAfterQuoteTrade =
    showQuoteTradeSection &&
    anyHomeAfter(showQuoteBankProductsSection, showQuoteHuidSection);
  const showDividerAfterQuoteBankProducts =
    showQuoteBankProductsSection && showQuoteHuidSection;

  const renderMasterDataBackButton = () => (
    <li className="sidebar-item">
      <button
        type="button"
        className={`sidebar-link d-flex align-items-center gap-2 w-100 border-0 bg-transparent text-start ${buttonClass}`}
        onClick={() => setShowMasterDataMenu(false)}
        aria-expanded="false"
      >
        <iconify-icon icon={SIDEBAR_ICONS.back} />
        <span className={MENU_LABEL_CLASS}>Back</span>
      </button>
    </li>
  );

  const renderMasterDataMenuButton = () =>
    hasAnyMasterDataPermission ? (
      <li className="sidebar-item">
        <button
          type="button"
          className={`sidebar-link d-flex align-items-center gap-2 w-100 border-0 bg-transparent text-start ${buttonClass}`}
          onClick={() => setShowMasterDataMenu(true)}
          aria-expanded="false"
        >
          <iconify-icon icon={SIDEBAR_ICONS.masterData} />
          <span className={MENU_LABEL_CLASS}>Master Data</span>
        </button>
      </li>
    ) : null;

  const renderMasterDataMenuItems = () => (
    <>
      {renderMasterDataBackButton()}
      {showDividerAfterMdBack && <SidebarDivider />}
      {renderMenuItem(
        PERMISSIONS.METAL_GROUP,
        <li
          className={`sidebar-item ${isActive("/metalgrouptable") ? "active" : ""
            }`}
        >
          <Link
            to="/metalgrouptable"
            className={getSidebarLinkClass("/metalgrouptable")}
            aria-expanded="false"
          >
            <iconify-icon icon={SIDEBAR_ICONS.metalGroup} />
            <span className={MENU_LABEL_CLASS}>Metal Group</span>
          </Link>
        </li>,
      )}
      {renderMenuItem(
        PERMISSIONS.ITEM_CATEGORY,
        <li
          className={`sidebar-item ${isActive("/itemcategorytable") ? "active" : ""
            }`}
        >
          <Link
            to="/itemcategorytable"
            className={getSidebarLinkClass("/itemcategorytable")}
            aria-expanded="false"
          >
            <iconify-icon icon={SIDEBAR_ICONS.itemCategory} />
            <span className={MENU_LABEL_CLASS}>Item Category</span>
          </Link>
        </li>,
      )}
      {renderMenuItem(
        null,
        <li
          className={`sidebar-item ${isActive("/otherchargestable") ? "active" : ""
            }`}
        >
          <Link
            to="/otherchargestable"
            className={getSidebarLinkClass("/otherchargestable")}
            aria-expanded="false"
          >
            <iconify-icon icon={SIDEBAR_ICONS.otherCharge} />
            <span className={MENU_LABEL_CLASS}>Other Charges</span>
          </Link>
        </li>,
        undefined,
        [
          PERMISSIONS.OTHER_CHARGE,
          PERMISSIONS.METAL_GROUP,
          PERMISSIONS.ITEM_CATEGORY,
          PERMISSIONS.PRODUCT_GROUP,
        ],
      )}



      {renderMenuItem(
        PERMISSIONS.PRODUCT_GROUP,
        <li
          className={`sidebar-item ${isActive("/productgrouptable") ? "active" : ""
            }`}
        >
          <Link
            to="/productgrouptable"
            className={getSidebarLinkClass("/productgrouptable")}
            aria-expanded="false"
          >
            <iconify-icon icon={SIDEBAR_ICONS.productGroup} />
            <span className={MENU_LABEL_CLASS}>Product Group</span>
          </Link>
        </li>,
      )}
      {renderMenuItem(
        PERMISSIONS.METAL_TRANSFER_VIEW,
        <li
          className={`sidebar-item ${isActive("/metaltransfer") ? "active" : ""
            }`}
        >
          <Link
            to="/metaltransfer"
            className={getSidebarLinkClass("/metaltransfer")}
            aria-expanded="false"
          >
            <iconify-icon icon={SIDEBAR_ICONS.metalTransfer} />
            <span className={MENU_LABEL_CLASS}>Metal Transfer</span>
          </Link>
        </li>,
      )}
      {showDividerAfterMdProductGroups && <SidebarDivider />}
      {renderMenuItem(
        PERMISSIONS.BANK,
        <li
          className={`sidebar-item ${isActive("/bankdetailstable") ? "active" : ""
            }`}
        >
          <Link
            to="/bankdetailstable"
            className={getSidebarLinkClass("/bankdetailstable")}
            aria-expanded="false"
          >
            <iconify-icon icon={SIDEBAR_ICONS.bank} />
            <span className={MENU_LABEL_CLASS}>Bank Accounts</span>
          </Link>
        </li>,
      )}
      {renderMenuItem(
        PERMISSIONS.CAPITAL_INVESTMENT_TABLE,
        <li
          className={`sidebar-item ${isActive("/capitalinvestment") ? "active" : ""
            }`}
        >
          <Link
            to="/capitalinvestment"
            className={getSidebarLinkClass("/capitalinvestment")}
            aria-expanded="false"
          >
            <iconify-icon icon={SIDEBAR_ICONS.capitalInvestment} />
            <span className={MENU_LABEL_CLASS}>Capital Investments</span>
          </Link>
        </li>,
      )}
      {renderMenuItem(
        null,
        <li
          className={`sidebar-item ${isActive("/shopexpense") ? "active" : ""
            }`}
        >
          <Link
            to="/shopexpense"
            className={getSidebarLinkClass("/shopexpense")}
            aria-expanded="false"
          >
            <iconify-icon icon={SIDEBAR_ICONS.shopExpense} />
            <span className={MENU_LABEL_CLASS}>Shop Expense</span>
          </Link>
        </li>,
        undefined,
        [PERMISSIONS.SHOP_EXPENSE_TABLE, PERMISSIONS.ADD_SHOP_EXPENSE],
      )}
      {showDividerAfterMdFinance && <SidebarDivider />}
      {renderMenuItem(
        null,
        <li
          className={`sidebar-item ${isActive("/salestarget") ? "active" : ""}`}
        >
          <Link
            className={getSidebarLinkClass("/salestarget")}
            to="/salestarget"
            aria-expanded="false"
          >
            <iconify-icon icon={SIDEBAR_ICONS.salesTarget} />
            <span className={MENU_LABEL_CLASS}>Sales Target</span>
          </Link>
        </li>,
        undefined,
        [
          PERMISSIONS.SALES_TARGET_VIEW,
          PERMISSIONS.SALES_TARGET_ADD,
          PERMISSIONS.SALES_TARGET_EDIT,
        ],
      )}
      {renderMenuItem(
        null,
        <li className={`sidebar-item ${isActive("/hrms") ? "active" : ""}`}>
          <Link
            className={getSidebarLinkClass("/hrms")}
            to="/hrms"
            aria-expanded="false"
          >
            <iconify-icon icon={SIDEBAR_ICONS.hrms} />
            <span className={MENU_LABEL_CLASS}>HRMS</span>
          </Link>
        </li>,
        undefined,
        HRMS_ROUTE_ANY_PERMISSIONS,
      )}
      {showDividerAfterMdSalesHrms && <SidebarDivider />}
      {renderMenuItem(
        PERMISSIONS.CUSTOMER,
        <li
          className={`sidebar-item ${isActive("/customertable") ? "active" : ""
            }`}
        >
          <Link
            className={getSidebarLinkClass("/customertable")}
            to="/customertable"
            aria-expanded="false"
          >
            <iconify-icon icon={SIDEBAR_ICONS.customer} />
            <span className={MENU_LABEL_CLASS}>Customers</span>
          </Link>
        </li>,
      )}
      {renderMenuItem(
        PERMISSIONS.SUPPLIER,
        <li className={`sidebar-item ${isActive("/karigar") ? "active" : ""}`}>
          <Link
            to="/karigar"
            className={getSidebarLinkClass("/karigar")}
            aria-expanded="false"
          >
            <iconify-icon icon={SIDEBAR_ICONS.supplierKarigar} />
            <span className={MENU_LABEL_CLASS}>Supplier/Karigar</span>
          </Link>
        </li>,
      )}
      {showDividerAfterMdParties && <SidebarDivider />}
      {renderMenuItem(
        null,
        <li
          className={`sidebar-item ${isActive("/tenantbranch") ? "active" : ""
            }`}
        >
          <Link
            to="/tenantbranch"
            className={getSidebarLinkClass("/tenantbranch")}
            aria-expanded="false"
          >
            <iconify-icon icon={SIDEBAR_ICONS.tenantBranch} />
            <span className={MENU_LABEL_CLASS}>Tenant & Branch</span>
          </Link>
        </li>,
        null,
        [PERMISSIONS.TENANT_TABLE, PERMISSIONS.BRANCH_TABLE],
      )}
      {showDividerAfterMdTenant && <SidebarDivider />}
      {renderMenuItem(
        PERMISSIONS.ROLE_VIEW,
        <li
          className={`sidebar-item ${isActive("/roletable") ? "active" : ""
            }`}
        >
          <Link
            to="/roletable"
            className={getSidebarLinkClass("/roletable")}
            aria-expanded="false"
          >
            <iconify-icon icon={SIDEBAR_ICONS.role} />
            <span className={MENU_LABEL_CLASS}>Role</span>
          </Link>
        </li>,
      )}
      {renderMenuItem(
        PERMISSIONS.EMPLOYEE,
        <li className={`sidebar-item ${isActive("/employee") ? "active" : ""}`}>
          <Link
            to="/employee"
            className={getSidebarLinkClass("/employee")}
            aria-expanded="false"
          >
            <iconify-icon icon={SIDEBAR_ICONS.employee} />
            <span className={MENU_LABEL_CLASS}>Employee</span>
          </Link>
        </li>,
      )}
    </>
  );

  return (
    <>
      <aside className="left-sidebar with-vertical d-flex flex-column overflow-hidden">
        <div className="brand-logo d-flex align-items-center justify-content-center flex-shrink-0">
          <Link
            to={
              isWhatsAppModule
                ? "/whatsapp/inbox"
                : isDihranModule
                  ? "/girvi"
                  : "/dashboard"
            }
            className="logo-img d-flex align-items-center justify-content-center"
            aria-label={`${tenantNameDisplay || "Home"} home`}
          >
            {(() => {
              const expandedLogoUrl = getTenantBrandImageSrc("logo");
              return expandedLogoUrl ? (
                <img
                  key={tenantBranding?.logo || "public-logo"}
                  src={expandedLogoUrl}
                  alt={tenantNameDisplay || "Home"}
                  className={`logo-full object-fit-contain ${isSidebarCollapsed ? "d-none" : "d-inline-flex"
                    }`}
                  draggable={false}
                />
              ) : (
                <ThemeTintedLogo
                  src={EXPANDED_LOGO_SRC}
                  alt={tenantNameDisplay || "Home"}
                  className={`logo-full ${isSidebarCollapsed ? "d-none" : "d-inline-flex"
                    }`}
                  sizerClassName="logo-full object-fit-contain"
                />
              );
            })()}
            {(() => {
              const sidebarIconUrl = getTenantBrandImageSrc("sidebar_icon");
              return sidebarIconUrl ? (
                <img
                  key={tenantBranding?.sidebar_icon || "public-sidebar-icon"}
                  src={sidebarIconUrl}
                  alt={tenantNameDisplay || "Home"}
                  className={`logo-mini mx-auto object-fit-contain ${isSidebarCollapsed ? "d-inline-flex" : "d-none"
                    }`}
                  style={{ width: 36, height: 36 }}
                  draggable={false}
                />
              ) : (
                <ThemeTintedLogo
                  src={COLLAPSED_LOGO_SRC}
                  alt={tenantNameDisplay || "Home"}
                  className={`logo-mini mx-auto ${isSidebarCollapsed ? "d-inline-flex" : "d-none"
                    }`}
                  sizerClassName="logo-mini object-fit-contain"
                  width={36}
                  height={36}
                />
              );
            })()}
          </Link>
        </div>
        {!isDihranModule && !isWhatsAppModule && !isSchemeModule ? (
          <div className="sidebar-search-wrap flex-shrink-0">
            <div className="sidebar-search-field">
              <iconify-icon
                icon="solar:magnifer-line-duotone"
                className="sidebar-search-icon"
                aria-hidden="true"
              />
              <input
                ref={sidebarSearchRef}
                type="search"
                className="sidebar-search-input"
                placeholder="Search pages..."
                value={menuSearch}
                onChange={(event) => setMenuSearch(event.target.value)}
                aria-label="Search sidebar pages"
                autoComplete="off"
                spellCheck={false}
              />
              {menuSearch ? (
                <button
                  type="button"
                  className="sidebar-search-clear"
                  onClick={() => {
                    setMenuSearch("");
                    sidebarSearchRef.current?.focus();
                  }}
                  aria-label="Clear search"
                >
                  <iconify-icon icon="solar:close-circle-line-duotone" />
                </button>
              ) : null}
            </div>
            {menuSearchNoResults ? (
              <p className="sidebar-search-empty">No pages found</p>
            ) : null}
          </div>
        ) : null}
        {/* ---------------------------------- */}
        {/* Dashboard */}
        {/* ---------------------------------- */}
        <nav
          ref={sidebarNavRef}
          className={[
            "sidebar-nav",
            "scroll-sidebar",
            "flex-grow-1",
            "overflow-auto",
            "app-scroll--idle-hidden",
            isSidebarNavScrolling ? "app-scroll--scrolling" : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <ul className="sidebar-menu" id="sidebarnav">
            {isDihranModule ? (
              <>
                {/* Girvi Module Menu */}
                <li
                  className={`sidebar-item ${isActive("/girvi") &&
                    !location.pathname.includes("/addgirvi")
                    ? "active"
                    : ""
                    }`}
                >
                  <Link
                    className={`sidebar-link d-flex align-items-center gap-2 ${isActive("/girvi") &&
                      !location.pathname.includes("/addgirvi")
                      ? "active"
                      : ""
                      }`}
                    to="/girvi"
                    aria-expanded="false"
                  >
                    <iconify-icon
                      icon={SIDEBAR_ICONS.girviDashboard}
                      className=""
                    />
                    <span className={MENU_LABEL_CLASS}>Dashboard</span>
                  </Link>
                </li>

                {(showGirviTable || showGirviQuickPayment || showGirviBank) ? (
                  <SidebarDivider />
                ) : null}

                {/* Girvi List */}
                {showGirviTable ? (
                  <li
                    className={`sidebar-item ${isActive("/girvitable") ? "active" : ""
                      }`}
                  >
                    <Link
                      className={`sidebar-link d-flex align-items-center gap-2 ${isActive("/girvitable") ? "active" : ""
                        }`}
                      to="/girvitable"
                      aria-expanded="false"
                    >
                      <iconify-icon icon={SIDEBAR_ICONS.girviList} className="" />
                      <span className={MENU_LABEL_CLASS}>Dihran</span>
                    </Link>
                  </li>
                ) : null}

                {showGirviTable &&
                  (showGirviQuickPayment || showGirviBank) ? (
                  <SidebarDivider />
                ) : null}

                {/* Quick Payment Entry */}
                {showGirviQuickPayment ? (
                  <li
                    className={`sidebar-item ${isActive("/girviquickpayment") ? "active" : ""
                      }`}
                  >
                    <Link
                      className={`sidebar-link d-flex align-items-center gap-2 ${isActive("/girviquickpayment") ? "active" : ""
                        }`}
                      to="/girviquickpayment"
                      aria-expanded="false"
                    >
                      <iconify-icon
                        icon={SIDEBAR_ICONS.girviQuickPayment}
                        className=""
                      />
                      <span className={MENU_LABEL_CLASS}>Quick Payment</span>
                    </Link>
                  </li>
                ) : null}

                {/* Bank Details */}
                {showGirviBank ? (
                  <li
                    className={`sidebar-item ${isActive("/girvibankdetails") ? "active" : ""
                      }`}
                  >
                    <Link
                      className={`sidebar-link d-flex align-items-center gap-2 ${isActive("/girvibankdetails") ? "active" : ""
                        }`}
                      to="/girvibankdetails"
                      aria-expanded="false"
                    >
                      <iconify-icon icon={SIDEBAR_ICONS.girviBank} />
                      <span className={MENU_LABEL_CLASS}>Bank</span>
                    </Link>
                  </li>
                ) : null}

                {(showReverseGirviDashboard ||
                  showReverseGirviTable ||
                  showReverseGirviQuickPayment) ? (
                  <SidebarDivider />
                ) : null}

                {showReverseGirviDashboard ? (
                  <li
                    className={`sidebar-item ${isActive("/reversegirvi") ? "active" : ""
                      }`}
                  >
                    <Link
                      className={`sidebar-link d-flex align-items-center gap-2 ${isActive("/reversegirvi") ? "active" : ""
                        }`}
                      to="/reversegirvi"
                      aria-expanded="false"
                    >
                      <iconify-icon icon={SIDEBAR_ICONS.girviDashboard} />
                      <span className={MENU_LABEL_CLASS}>Reverse Dashboard</span>
                    </Link>
                  </li>
                ) : null}

                {showReverseGirviTable ? (
                  <li
                    className={`sidebar-item ${isActive("/reversegirvitable") ||
                      isActive("/addreversegirvi") ||
                      isActive("/editreversegirvi")
                      ? "active"
                      : ""
                      }`}
                  >
                    <Link
                      className={`sidebar-link d-flex align-items-center gap-2 ${isActive("/reversegirvitable") ||
                        isActive("/addreversegirvi") ||
                        isActive("/editreversegirvi")
                        ? "active"
                        : ""
                        }`}
                      to="/reversegirvitable"
                      aria-expanded="false"
                    >
                      <iconify-icon icon={SIDEBAR_ICONS.girviList} />
                      <span className={MENU_LABEL_CLASS}>Reverse Dihran</span>
                    </Link>
                  </li>
                ) : null}

                {showReverseGirviQuickPayment ? (
                  <li
                    className={`sidebar-item ${isActive("/reversegirviquickpayment") ? "active" : ""
                      }`}
                  >
                    <Link
                      className={`sidebar-link d-flex align-items-center gap-2 ${isActive("/reversegirviquickpayment") ? "active" : ""
                        }`}
                      to="/reversegirviquickpayment"
                      aria-expanded="false"
                    >
                      <iconify-icon icon={SIDEBAR_ICONS.girviQuickPayment} />
                      <span className={MENU_LABEL_CLASS}>Pay to Customer</span>
                    </Link>
                  </li>
                ) : null}

                {/* Weekly/Monthly Payment Entry */}

                {/* <li>
                      <span className="sidebar-divider lg" />
                    </li> */}

                {/* Reports */}
                {/* <li
                      className={`sidebar-item ${
                        isActive("/girvi/reports") ? "active" : ""
                      }`}
                    >
                      <Link
                        className={`sidebar-link d-flex align-items-center gap-2 ${
                          isActive("/girvi/reports") ? "active" : ""
                        }`}
                        to="/girvi/reports"
                        aria-expanded="false"
                      >
                        <iconify-icon
                          icon="solar:chart-2-linear"
                          className=""
                        />
                        <span className={MENU_LABEL_CLASS}>Reports</span>
                      </Link>
                    </li> */}
              </>
            ) : isWhatsAppModule ? (
              <>
                <li className="sidebar-item">
                  <Link
                    to="/dashboard"
                    className={`sidebar-link d-flex align-items-center gap-2 ${buttonClass}`}
                    aria-expanded="false"
                  >
                    <iconify-icon icon={SIDEBAR_ICONS.back} />
                    <span className={MENU_LABEL_CLASS}>Back</span>
                  </Link>
                </li>
                <SidebarDivider />
                {showWhatsAppDashboard ? (
                  <li
                    className={`sidebar-item ${isWhatsAppPathActive("/whatsapp/dashboard")
                      ? "active"
                      : ""
                      }`}
                  >
                    <Link
                      className={getSidebarLinkClass("/whatsapp/dashboard")}
                      to="/whatsapp/dashboard"
                      aria-expanded="false"
                    >
                      <iconify-icon icon={SIDEBAR_ICONS.waDashboard} />
                      <span className={MENU_LABEL_CLASS}>Dashboard</span>
                    </Link>
                  </li>
                ) : null}
                {showWhatsAppTemplates ? (
                  <li
                    className={`sidebar-item ${isWhatsAppPathActive("/whatsapp/templates")
                      ? "active"
                      : ""
                      }`}
                  >
                    <Link
                      className={getSidebarLinkClass("/whatsapp/templates")}
                      to="/whatsapp/templates"
                      aria-expanded="false"
                    >
                      <iconify-icon icon={SIDEBAR_ICONS.waTemplates} />
                      <span className={MENU_LABEL_CLASS}>My Template</span>
                    </Link>
                  </li>
                ) : null}
                {showWhatsAppCampaigns ? (
                  <li
                    className={`sidebar-item ${isWhatsAppPathActive("/whatsapp/campaigns")
                      ? "active"
                      : ""
                      }`}
                  >
                    <Link
                      className={getSidebarLinkClass("/whatsapp/campaigns")}
                      to="/whatsapp/campaigns"
                      aria-expanded="false"
                    >
                      <iconify-icon icon={SIDEBAR_ICONS.waCampaigns} />
                      <span className={MENU_LABEL_CLASS}>My Campaign</span>
                    </Link>
                  </li>
                ) : null}
                {showWhatsAppInbox ? (
                  <li
                    className={`sidebar-item ${isWhatsAppPathActive("/whatsapp/inbox") ? "active" : ""
                      }`}
                  >
                    <Link
                      className={getSidebarLinkClass("/whatsapp/inbox")}
                      to="/whatsapp/inbox"
                      aria-expanded="false"
                    >
                      <iconify-icon icon={SIDEBAR_ICONS.waInbox} />
                      <span className={MENU_LABEL_CLASS}>Inbox</span>
                    </Link>
                  </li>
                ) : null}
                {showWhatsAppPhonebooks ? (
                  <li
                    className={`sidebar-item ${isWhatsAppPathActive("/whatsapp/phonebooks")
                      ? "active"
                      : ""
                      }`}
                  >
                    <Link
                      className={getSidebarLinkClass("/whatsapp/phonebooks")}
                      to="/whatsapp/phonebooks"
                      aria-expanded="false"
                    >
                      <iconify-icon icon={SIDEBAR_ICONS.waPhonebooks} />
                      <span className={MENU_LABEL_CLASS}>Phonebook</span>
                    </Link>
                  </li>
                ) : null}
              </>
            ) : isSchemeModule ? (
              <>
                {/* Scheme Module Menu */}
                <li className="sidebar-item">
                  <Link
                    to="/dashboard"
                    className={`sidebar-link d-flex align-items-center gap-2 ${buttonClass}`}
                    aria-expanded="false"
                  >
                    <iconify-icon icon={SIDEBAR_ICONS.back} />
                    <span className={MENU_LABEL_CLASS}>Back</span>
                  </Link>
                </li>
                <SidebarDivider />
                {renderMenuItem(
                  null,
                  <li
                    className={`sidebar-item ${isActive("/scheme/dashboard") ? "active" : ""
                      }`}
                  >
                    <Link
                      className={getSidebarLinkClass("/scheme/dashboard")}
                      to="/scheme/dashboard"
                      aria-expanded="false"
                    >
                      <iconify-icon icon={SIDEBAR_ICONS.schemeDashboard} />
                      <span className={MENU_LABEL_CLASS}>Dashboard</span>
                    </Link>
                  </li>,
                  null,
                  [PERMISSIONS.SCHEME_VIEW, PERMISSIONS.SCHEME_PLAN_VIEW],
                )}
                {renderMenuItem(
                  PERMISSIONS.SCHEME_PLAN_VIEW,
                  <li
                    className={`sidebar-item ${isActive("/scheme/plans") ? "active" : ""
                      }`}
                  >
                    <Link
                      className={getSidebarLinkClass("/scheme/plans")}
                      to="/scheme/plans"
                      aria-expanded="false"
                    >
                      <iconify-icon icon={SIDEBAR_ICONS.schemePlans} />
                      <span className={MENU_LABEL_CLASS}>Scheme Plan</span>
                    </Link>
                  </li>,
                )}
                {renderMenuItem(
                  PERMISSIONS.SCHEME_VIEW,
                  <li
                    className={`sidebar-item ${isActive("/scheme/cards") ? "active" : ""
                      }`}
                  >
                    <Link
                      className={getSidebarLinkClass("/scheme/cards")}
                      to="/scheme/cards"
                      aria-expanded="false"
                    >
                      <iconify-icon icon={SIDEBAR_ICONS.schemeCards} />
                      <span className={MENU_LABEL_CLASS}>Scheme Card List</span>
                    </Link>
                  </li>,
                )}
                {renderMenuItem(
                  PERMISSIONS.SCHEME_VIEW,
                  <li
                    className={`sidebar-item ${isActive("/scheme/transactions") ? "active" : ""
                      }`}
                  >
                    <Link
                      className={getSidebarLinkClass("/scheme/transactions")}
                      to="/scheme/transactions"
                      aria-expanded="false"
                    >
                      <iconify-icon icon={SIDEBAR_ICONS.schemeTransactions} />
                      <span className={MENU_LABEL_CLASS}>Payment Transaction</span>
                    </Link>
                  </li>,
                )}
              </>
            ) : isQuotationModule ? (
              <>
                {renderMenuItem(
                  PERMISSIONS.DASHBOARD,
                  <li
                    className={`sidebar-item ${isActive("/dashboard") ? "active" : ""
                      }`}
                  >
                    <Link
                      className={getSidebarLinkClass("/dashboard")}
                      to="/dashboard"
                      aria-expanded="false"
                    >
                      <iconify-icon
                        icon={SIDEBAR_ICONS.dashboard}
                        className=""
                      />
                      <span className={MENU_LABEL_CLASS}>Dashboard</span>
                    </Link>
                  </li>,
                )}

                {showDividerAfterQuoteTop && (
                  <li>
                    <span className="sidebar-divider lg" />
                  </li>
                )}

                {renderMenuItem(
                  null,
                  <li
                    className={`sidebar-item ${isActive("/invoice") ? "active" : ""
                      }`}
                  >
                    <Link
                      to="/invoice"
                      className={getSidebarLinkClass("/invoice")}
                      aria-expanded="false"
                    >
                      <iconify-icon icon={SIDEBAR_ICONS.sales} />
                      <span className={MENU_LABEL_CLASS}>Sales</span>
                    </Link>
                  </li>,
                  null,
                  [PERMISSIONS.SALES, PERMISSIONS.INVOICE],
                )}
                {renderMenuItem(
                  null,
                  <li
                    className={`sidebar-item ${isActive("/whinvoice") || isActive("/whsales") || isActive("/editwhsale") ? "active" : ""
                      }`}
                  >
                    <Link
                      to="/whinvoice"
                      className={getSidebarLinkClass("/whinvoice")}
                      aria-expanded="false"
                    >
                      <iconify-icon icon={SIDEBAR_ICONS.wholesale} />
                      <span className={MENU_LABEL_CLASS}>Wh Sales</span>
                    </Link>
                  </li>,
                  null,
                  [PERMISSIONS.WHSALE_VIEW],
                )}
                {renderMenuItem(
                  PERMISSIONS.CUSTOM_ORDER_ENTRY_TABLE,
                  <li
                    className={`sidebar-item ${isActive("/customorderentrytable") ? "active" : ""
                      }`}
                  >
                    <Link
                      to="/customorderentrytable"
                      className={getSidebarLinkClass("/customorderentrytable")}
                      aria-expanded="false"
                    >
                      <iconify-icon icon={SIDEBAR_ICONS.customOrder} />
                      <span className={MENU_LABEL_CLASS}>Custom order</span>
                    </Link>
                  </li>,
                )}
                {renderMenuItem(
                  PERMISSIONS.REPAIRING_ORDER,
                  <li
                    className={`sidebar-item ${isActive("/repairingorder") ? "active" : ""
                      }`}
                  >
                    <Link
                      to="/repairingorder"
                      className={getSidebarLinkClass("/repairingorder")}
                      aria-expanded="false"
                    >
                      <iconify-icon icon={SIDEBAR_ICONS.repairingOrder} />
                      <span className={MENU_LABEL_CLASS}>Repair order</span>
                    </Link>
                  </li>,
                )}

                {showDividerAfterQuoteTrade && (
                  <li>
                    <span className="sidebar-divider lg" />
                  </li>
                )}

                {showBankQuickActions && (
                  <>
                    <li
                      className={`sidebar-item ${showBankPopup && bankFlowType === "bank-in"
                        ? "active"
                        : ""
                        }`}
                    >
                      <button
                        type="button"
                        className={`sidebar-link d-flex align-items-center gap-2 w-100 border-0 bg-transparent text-start ${buttonClass}`}
                        onClick={() => openBankPopup("bank-in")}
                        aria-expanded="false"
                      >
                        <iconify-icon icon={SIDEBAR_ICONS.bankIn} />
                        <span className={MENU_LABEL_CLASS}>Bank-IN</span>
                      </button>
                    </li>
                    <li
                      className={`sidebar-item ${showBankPopup && bankFlowType === "bank-out"
                        ? "active"
                        : ""
                        }`}
                    >
                      <button
                        type="button"
                        className={`sidebar-link d-flex align-items-center gap-2 w-100 border-0 bg-transparent text-start ${buttonClass}`}
                        onClick={() => openBankPopup("bank-out")}
                        aria-expanded="false"
                      >
                        <iconify-icon icon={SIDEBAR_ICONS.bankOut} />
                        <span className={MENU_LABEL_CLASS}>Bank-OUT</span>
                      </button>
                    </li>
                    <li
                      className={`sidebar-item ${isActive("/paymenttransaction") ? "active" : ""
                        }`}
                    >
                      <Link
                        to="/paymenttransaction"
                        className={getSidebarLinkClass("/paymenttransaction")}
                        aria-expanded="false"
                      >
                        <iconify-icon icon={SIDEBAR_ICONS.paymentTransaction} />
                        <span className={MENU_LABEL_CLASS}>
                          Transactions
                        </span>
                      </Link>
                    </li>
                  </>
                )}

                {!showProductsInNavbar &&
                  renderMenuItem(
                    PERMISSIONS.STOCK_TABLE,
                    <li
                      className={`sidebar-item ${isActive("/stocktable") ? "active" : ""
                        }`}
                    >
                      <Link
                        to="/stocktable"
                        className={getSidebarLinkClass("/stocktable")}
                        aria-expanded="false"
                      >
                        <iconify-icon icon={SIDEBAR_ICONS.products} />
                        <span className={MENU_LABEL_CLASS}>Products</span>
                      </Link>
                    </li>,
                  )}

                {showDividerAfterQuoteBankProducts && (
                  <li>
                    <span className="sidebar-divider lg" />
                  </li>
                )}

                {!showHuidInNavbar &&
                  renderMenuItem(
                    PERMISSIONS.PRODUCT_SEARCH_VIEW,
                    <li
                      className={`sidebar-item ${isActive("/huidbarcodesearch") ? "active" : ""
                        }`}
                    >
                      <Link
                        to="/huidbarcodesearch"
                        className={getSidebarLinkClass("/huidbarcodesearch")}
                        aria-expanded="false"
                      >
                        <iconify-icon icon={SIDEBAR_ICONS.huidBarcode} />
                        <span className={MENU_LABEL_CLASS}>HUID/Barcode</span>
                      </Link>
                    </li>,
                  )}
              </>
            ) : showMasterDataMenu ? (
              renderMasterDataMenuItems()
            ) : (
              <>
                {renderMenuItem(
                  PERMISSIONS.DASHBOARD,
                  <li
                    className={`sidebar-item ${isActive("/dashboard") ? "active" : ""
                      }`}
                  >
                    <Link
                      className={getSidebarLinkClass("/dashboard")}
                      to="/dashboard"
                      aria-expanded="false"
                    >
                      <iconify-icon icon={SIDEBAR_ICONS.dashboard} />
                      <span className={MENU_LABEL_CLASS}>Dashboard</span>
                    </Link>
                  </li>,
                )}

                {renderMenuItem(
                  PERMISSIONS.DAILY_PRICE,
                  <li
                    className={`sidebar-item ${isActive("/dailyprice") ? "active" : ""
                      }`}
                  >
                    <Link
                      className={getSidebarLinkClass("/dailyprice")}
                      to="/dailyprice"
                      aria-expanded="false"
                    >
                      <iconify-icon icon={SIDEBAR_ICONS.dailyPrice} />
                      <span className={MENU_LABEL_CLASS}>Daily Price</span>
                    </Link>
                  </li>,
                  "DAILY_PRICE",
                )}

                {showDividerAfterHomeTop && <SidebarDivider />}
                {renderMasterDataMenuButton()}

                {/* Scheme Module Entry */}
                {renderMenuItem(
                  null,
                  <li className="sidebar-item">
                    <Link
                      to="/scheme/dashboard"
                      className={`sidebar-link d-flex align-items-center gap-2 ${buttonClass}`}
                      aria-expanded="false"
                    >
                      <iconify-icon icon={SIDEBAR_ICONS.scheme} />
                      <span className={MENU_LABEL_CLASS}>Scheme</span>
                    </Link>
                  </li>,
                  null,
                  [
                    PERMISSIONS.SCHEME_VIEW,
                    PERMISSIONS.SCHEME_PLAN_VIEW,
                  ],
                )}

                {showDividerAfterHomeMaster && <SidebarDivider />}

                {renderMenuItem(
                  PERMISSIONS.CUSTOMER_ARRIVAL,
                  <li
                    className={`sidebar-item ${isActive("/customerwalkin") ? "active" : ""
                      }`}
                  >
                    <Link
                      className={getSidebarLinkClass("/customerwalkin")}
                      to="/customerwalkin"
                      aria-expanded="false"
                    >
                      <iconify-icon icon={SIDEBAR_ICONS.walkIn} />
                      <span className={MENU_LABEL_CLASS}>Walk-In</span>
                    </Link>
                  </li>,
                )}
                {renderMenuItem(
                  PERMISSIONS.CUSTOMER_ASSIGN,
                  <li
                    className={`sidebar-item ${isActive("/customerassigntable") ? "active" : ""
                      }`}
                  >
                    <Link
                      className={getSidebarLinkClass("/customerassigntable")}
                      to="/customerassigntable"
                      aria-expanded="false"
                    >
                      <iconify-icon icon={SIDEBAR_ICONS.customerAssign} />
                      <span className={MENU_LABEL_CLASS}>Customer Assign</span>
                    </Link>
                  </li>,
                )}
                {renderMenuItem(
                  PERMISSIONS.ORDER_QUEUE,
                  <li
                    className={`sidebar-item ${isActive("/orderqueue") ? "active" : ""
                      }`}
                  >
                    <Link
                      className={getSidebarLinkClass("/orderqueue")}
                      to="/orderqueue"
                      aria-expanded="false"
                    >
                      <iconify-icon icon={SIDEBAR_ICONS.orderQueue} />
                      <span className={MENU_LABEL_CLASS}>Order Queue</span>
                    </Link>
                  </li>,
                )}

                {showDividerAfterHomeWalkIn && <SidebarDivider />}

                {showBankQuickActions && (
                  <>
                    <li
                      className={`sidebar-item ${showBankPopup && bankFlowType === "bank-in"
                        ? "active"
                        : ""
                        }`}
                    >
                      <button
                        type="button"
                        className={`sidebar-link d-flex align-items-center gap-2 w-100 border-0 bg-transparent text-start ${buttonClass}`}
                        onClick={() => openBankPopup("bank-in")}
                        aria-expanded="false"
                      >
                        <iconify-icon icon={SIDEBAR_ICONS.bankIn} />
                        <span className={MENU_LABEL_CLASS}>Bank In</span>
                      </button>
                    </li>
                    <li
                      className={`sidebar-item ${showBankPopup && bankFlowType === "bank-out"
                        ? "active"
                        : ""
                        }`}
                    >
                      <button
                        type="button"
                        className={`sidebar-link d-flex align-items-center gap-2 w-100 border-0 bg-transparent text-start ${buttonClass}`}
                        onClick={() => openBankPopup("bank-out")}
                        aria-expanded="false"
                      >
                        <iconify-icon icon={SIDEBAR_ICONS.bankOut} />
                        <span className={MENU_LABEL_CLASS}>Bank Out</span>
                      </button>
                    </li>
                    <li
                      className={`sidebar-item ${isActive("/paymenttransaction") ? "active" : ""
                        }`}
                    >
                      <Link
                        to="/paymenttransaction"
                        className={getSidebarLinkClass("/paymenttransaction")}
                        aria-expanded="false"
                      >
                        <iconify-icon icon={SIDEBAR_ICONS.paymentTransaction} />
                        <span className={MENU_LABEL_CLASS}>
                          Transactions
                        </span>
                      </Link>
                    </li>
                  </>
                )}

                {showDividerAfterHomeBank && <SidebarDivider />}

                {renderMenuItem(
                  null,
                  <li
                    className={`sidebar-item ${isActive("/purchasetable") ? "active" : ""
                      }`}
                  >
                    <Link
                      to="/purchasetable"
                      className={getSidebarLinkClass("/purchasetable")}
                      aria-expanded="false"
                    >
                      <iconify-icon icon={SIDEBAR_ICONS.purchase} />
                      <span className={MENU_LABEL_CLASS}>Purchase</span>
                    </Link>
                  </li>,
                  null,
                  [PERMISSIONS.PURCHASE_TABLE, PERMISSIONS.ADD_PURCHASE],
                )}
                {renderMenuItem(
                  PERMISSIONS.METAL_PURCHASE_TABLE,
                  <li
                    className={`sidebar-item ${isActive("/metalpurchasetable") ? "active" : ""
                      }`}
                  >
                    <Link
                      to="/metalpurchasetable"
                      className={getSidebarLinkClass("/metalpurchasetable")}
                      aria-expanded="false"
                    >
                      <iconify-icon icon={SIDEBAR_ICONS.oldMetalPurchase} />
                      <span className={MENU_LABEL_CLASS}>
                        Old Metal Purchase
                      </span>
                    </Link>
                  </li>,
                )}
                {renderMenuItem(
                  null,
                  <li
                    className={`sidebar-item ${isActive("/invoice") ? "active" : ""
                      }`}
                  >
                    <Link
                      to="/invoice"
                      className={getSidebarLinkClass("/invoice")}
                      aria-expanded="false"
                    >
                      <iconify-icon icon={SIDEBAR_ICONS.sales} />
                      <span className={MENU_LABEL_CLASS}>Sales</span>
                    </Link>
                  </li>,
                  null,
                  [PERMISSIONS.SALES, PERMISSIONS.INVOICE],
                )}
                {renderMenuItem(
                  null,
                  <li
                    className={`sidebar-item ${isActive("/whinvoice") || isActive("/whsales") || isActive("/editwhsale") ? "active" : ""
                      }`}
                  >
                    <Link
                      to="/whinvoice"
                      className={getSidebarLinkClass("/whinvoice")}
                      aria-expanded="false"
                    >
                      <iconify-icon icon={SIDEBAR_ICONS.wholesale} />
                      <span className={MENU_LABEL_CLASS}>Wh Sales</span>
                    </Link>
                  </li>,
                  null,
                  [PERMISSIONS.WHSALE_VIEW],
                )}
                {renderMenuItem(
                  PERMISSIONS.CUSTOM_ORDER_ENTRY_TABLE,
                  <li
                    className={`sidebar-item ${isActive("/customorderentrytable") ? "active" : ""
                      }`}
                  >
                    <Link
                      to="/customorderentrytable"
                      className={getSidebarLinkClass("/customorderentrytable")}
                      aria-expanded="false"
                    >
                      <iconify-icon icon={SIDEBAR_ICONS.customOrder} />
                      <span className={MENU_LABEL_CLASS}>Custom Order</span>
                    </Link>
                  </li>,
                )}
                {renderMenuItem(
                  PERMISSIONS.REPAIRING_ORDER,
                  <li
                    className={`sidebar-item ${isActive("/repairingorder") ? "active" : ""
                      }`}
                  >
                    <Link
                      to="/repairingorder"
                      className={getSidebarLinkClass("/repairingorder")}
                      aria-expanded="false"
                    >
                      <iconify-icon icon={SIDEBAR_ICONS.repairingOrder} />
                      <span className={MENU_LABEL_CLASS}>Repairing Order</span>
                    </Link>
                  </li>,
                )}
                {renderMenuItem(
                  PERMISSIONS.JANGAD,
                  <li
                    className={`sidebar-item ${isActive("/Jangad") ? "active" : ""
                      }`}
                  >
                    <Link
                      to="/Jangad"
                      className={getSidebarLinkClass("/Jangad")}
                      aria-expanded="false"
                    >
                      <iconify-icon icon={SIDEBAR_ICONS.jangad} />
                      <span className={MENU_LABEL_CLASS}>Jangad</span>
                    </Link>
                  </li>,
                )}

                {showDividerAfterHomeTrade && <SidebarDivider />}

                {renderMenuItem(
                  PERMISSIONS.PRODUCT_ASSORTING_TABLE,
                  <li
                    className={`sidebar-item ${isActive("/productassortingtable") ? "active" : ""
                      }`}
                  >
                    <Link
                      to="/productassortingtable"
                      className={getSidebarLinkClass("/productassortingtable")}
                      aria-expanded="false"
                    >
                      <iconify-icon icon={SIDEBAR_ICONS.productAssorting} />
                      <span className={MENU_LABEL_CLASS}>
                        Product Assorting
                      </span>
                    </Link>
                  </li>,
                )}
                {renderMenuItem(
                  PERMISSIONS.REFINERY,
                  <li
                    className={`sidebar-item ${location.pathname === "/refinery" ||
                      location.pathname.startsWith("/refinery/")
                      ? "active"
                      : ""
                      }`}
                  >
                    <Link
                      to="/refinery"
                      className={getSidebarLinkClass("/refinery")}
                      aria-expanded="false"
                    >
                      <iconify-icon icon={SIDEBAR_ICONS.refinery} />
                      <span className={MENU_LABEL_CLASS}>Refinery</span>
                    </Link>
                  </li>,
                )}
                {showDividerAfterHomeOps && <SidebarDivider />}

                {renderMenuItem(
                  PERMISSIONS.LEADS_VIEW,
                  <li
                    className={`sidebar-item ${location.pathname === "/leads" ||
                      location.pathname.startsWith("/leads/")
                      ? "active"
                      : ""
                      }`}
                  >
                    <Link
                      className={(() => {
                        const active =
                          location.pathname === "/leads" ||
                          location.pathname.startsWith("/leads/");
                        const baseClass = `sidebar-link d-flex align-items-center gap-2 ${active ? "active" : ""
                          }`;
                        if (!active) return baseClass;
                        const styleClass =
                          buttonTableStyle === "curve"
                            ? "active-curve"
                            : "active-square";
                        return `${baseClass} ${styleClass}`;
                      })()}
                      to="/leads"
                      aria-expanded="false"
                    >
                      <iconify-icon icon={SIDEBAR_ICONS.leads} />
                      <span className={MENU_LABEL_CLASS}>Leads</span>
                    </Link>
                  </li>,
                )}
                {renderMenuItem(
                  PERMISSIONS.REMINDERS_VIEW,
                  <li
                    className={`sidebar-item ${isActive("/reminders") || isActive("/followups")
                      ? "active"
                      : ""
                      }`}
                  >
                    <Link
                      className={getSidebarLinkClass("/reminders")}
                      to="/reminders"
                      aria-expanded="false"
                    >
                      <iconify-icon icon={SIDEBAR_ICONS.reminders} />
                      <span className={MENU_LABEL_CLASS}>Reminders</span>
                    </Link>
                  </li>,
                )}

                {showDividerAfterHomeCrm && <SidebarDivider />}

                {renderMenuItem(
                  null,
                  <li
                    className={`sidebar-item ${isActive("/addsettlement") ? "active" : ""
                      }`}
                  >
                    <Link
                      to="/addsettlement"
                      className={getSidebarLinkClass("/addsettlement")}
                      aria-expanded="false"
                    >
                      <iconify-icon icon={SIDEBAR_ICONS.addSettlement} />
                      <span className={MENU_LABEL_CLASS}>Customer Settlement</span>
                    </Link>
                  </li>,
                  null,
                  [
                    PERMISSIONS.CUSTOMER_SETTLEMENT_VIEW,
                    PERMISSIONS.CUSTOMER_SETTLEMENT_ADD,
                    PERMISSIONS.CUSTOMER_SETTLEMENT_EDIT,
                  ],
                )}
              </>
            )}
          </ul>
        </nav>
      </aside>

      <CashbookQuickPopup
        show={showCashPopup}
        onClose={closeCashPopup}
        cashFlowType={cashFlowType}
        buttonClass={buttonClass}
        branchId={
          userData?.branch_id || userData?.branchId || userData?.branch || 1
        }
      />
      <BankbookQuickPopup
        show={showBankPopup}
        onClose={closeBankPopup}
        bankFlowType={bankFlowType}
        buttonClass={buttonClass}
        branchId={
          userData?.branch_id || userData?.branchId || userData?.branch || 1
        }
      />
    </>
  );
}

export default Sidebar;
